/**
 * Rewrites store-switch links (`a[data-lang]`, e.g. in the #site-language sidebar)
 * so they point to the equivalent content on the target store.
 *
 * Each link declares its target via `data-store` ("pl" | "com" | "ch") and
 * `data-lang` (language path prefix on the target store). If `data-store` is
 * missing, the store is inferred from `data-lang` (e.g. "en-ch" -> "ch").
 *
 * Same-store language switches stay on the current page (hreflang alternate,
 * then locale-prefix swap). Cross-store switches use metafields.
 *
 * The body element exposes the alternative-content metafields as data attributes
 * (see layout/theme.liquid):
 * - data-alt-link    -> custom.alternative_link
 * - data-alt-link-ch -> custom.alternative_link_ch
 * - data-alt-link-en -> custom.alternative_link_en
 */

/**
 * Target stores: origin used on fallback pages and the language that lives
 * at the root path (no prefix). `null` means every language is prefixed.
 */
const TARGET_STORES = {
  pl: { origin: 'https://lyofood.pl', rootLang: 'pl' },
  com: { origin: 'https://lyofood.com', rootLang: 'en' },
  ch: { origin: 'https://lyofood.ch', rootLang: null },
};

const HOST_TO_STORE = {
  'lyofood.pl': 'pl',
  'lyofood.com': 'com',
  'lyofood.ch': 'ch',
};

/**
 * Which body dataset key (metafield) holds the alternative link for each
 * target store, depending on the store we are currently on.
 */
const METAFIELD_BY_HOST = {
  'lyofood.pl': { com: 'altLink', ch: 'altLinkCh' },
  'lyofood.com': { pl: 'altLink', ch: 'altLinkCh' },
  'lyofood.ch': { pl: 'altLink', com: 'altLinkEn' },
};

/** Host config used when the current hostname is unknown (e.g. theme preview). */
const DEFAULT_HOST = 'lyofood.com';

/**
 * @typedef {object} RedirectorContext
 * @property {object} metafieldMap - Dataset keys for the current host.
 * @property {DOMStringMap} bodyData - Body element dataset with metafield URLs.
 * @property {boolean} isFallbackPage - Whether the current path uses origin+path fallback.
 * @property {string} currentPath - Current page pathname.
 * @property {string} currentStoreKey - Store key for the current hostname.
 * @property {string[]} currentLangPrefixes - Locale prefixes used on the current store.
 * @property {string} unprefixedPath - Current pathname without the locale prefix.
 */

/**
 * Picks the target store key from the link attributes.
 *
 * @param {HTMLAnchorElement} link - Language menu link.
 * @returns {string} Store key: "pl", "com", or "ch".
 */
function resolveStoreKey(link) {
  const storeFromAttr = link.getAttribute('data-store');
  if (storeFromAttr && TARGET_STORES[storeFromAttr]) {
    return storeFromAttr;
  }

  const lang = link.getAttribute('data-lang') || '';
  if (lang.includes('-ch')) {
    return 'ch';
  }
  if (lang === 'pl') {
    return 'pl';
  }
  return 'com';
}

/**
 * Collects locale path prefixes declared in the language menu for a store.
 *
 * @param {string} storeKey - Store key: "pl", "com", or "ch".
 * @returns {string[]} Prefixes, longest first.
 */
function collectLangPrefixes(storeKey) {
  const store = TARGET_STORES[storeKey];
  const rootLang = store ? store.rootLang : null;
  const prefixes = [];

  document.querySelectorAll('#site-language a[data-lang]').forEach((link) => {
    if (resolveStoreKey(link) !== storeKey) {
      return;
    }
    const lang = link.getAttribute('data-lang');
    if (!lang || lang === rootLang) {
      return;
    }
    let exists = false;
    prefixes.forEach((prefix) => {
      if (prefix === lang) {
        exists = true;
      }
    });
    if (!exists) {
      prefixes.push(lang);
    }
  });

  return prefixes;
}

/**
 * Removes the store's locale prefix from a pathname.
 *
 * @param {string} pathname - Pathname that may include a locale prefix.
 * @param {string[]} prefixes - Locale prefixes for the store, longest first.
 * @returns {string} Pathname without the locale prefix.
 */
function stripLocalePrefix(pathname, prefixes) {
  let matchingPrefix = '';

  prefixes.forEach((prefix) => {
    const isExact = pathname === `/${prefix}` || pathname === `/${prefix}/`;
    const isNested = pathname.startsWith(`/${prefix}/`);
    if ((isExact || isNested) && prefix.length > matchingPrefix.length) {
      matchingPrefix = prefix;
    }
  });

  if (!matchingPrefix) {
    return pathname;
  }
  if (pathname === `/${matchingPrefix}` || pathname === `/${matchingPrefix}/`) {
    return '/';
  }
  return pathname.substring(matchingPrefix.length + 1);
}

/**
 * Builds a path on the target store from an unprefixed pathname.
 *
 * @param {string} unprefixedPath - Pathname without a locale prefix.
 * @param {string} targetLang - Language path prefix on the target store.
 * @param {boolean} needsPrefix - Whether the language lives under a path prefix.
 * @returns {string} Pathname including the target locale when required.
 */
function buildPrefixedPath(unprefixedPath, targetLang, needsPrefix) {
  if (!needsPrefix) {
    return unprefixedPath;
  }
  if (unprefixedPath === '/') {
    return `/${targetLang}`;
  }
  return `/${targetLang}${unprefixedPath}`;
}

/**
 * Finds Shopify's translated URL for a locale from hreflang alternates.
 *
 * @param {string} targetLang - Language path prefix, e.g. "en-ch".
 * @returns {string|null} Alternate href or null when none match.
 */
function findAlternateHref(targetLang) {
  const normalized = targetLang.toLowerCase().replace(/_/g, '-');
  let href = null;

  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => {
    if (href) {
      return;
    }
    const hreflang = (node.getAttribute('hreflang') || '').toLowerCase().replace(/_/g, '-');
    if (hreflang !== 'x-default' && hreflang === normalized) {
      href = node.getAttribute('href');
    }
  });

  return href;
}

/**
 * Builds the URL for switching language on the current store.
 *
 * @param {string} targetLang - Language path prefix on this store.
 * @param {boolean} needsPrefix - Whether the language lives under a path prefix.
 * @param {RedirectorContext} context - Current page context.
 * @returns {string} URL of the current page in the target language.
 */
function buildSameStoreHref(targetLang, needsPrefix, context) {
  const alternate = findAlternateHref(targetLang);
  if (alternate) {
    return alternate;
  }

  const targetPath = buildPrefixedPath(context.unprefixedPath, targetLang, needsPrefix);
  return `${window.location.origin}${targetPath}${window.location.search}`;
}

/**
 * Builds the alternative URL from a metafield link, using the target store
 * origin and prefixing the language when that store expects it.
 *
 * @param {string} baseLink - URL stored in the metafield.
 * @param {string} targetLang - Language path prefix on the target store.
 * @param {boolean} needsPrefix - Whether the language lives under a path prefix.
 * @param {string} origin - Origin of the target store (not the metafield host).
 * @returns {string|null} Final URL or null when the metafield URL is invalid.
 */
function buildAlternativeHref(baseLink, targetLang, needsPrefix, origin) {
  try {
    const url = new URL(baseLink);
    let newPath = url.pathname;

    const hasPrefix = newPath === `/${targetLang}` || newPath.startsWith(`/${targetLang}/`);
    if (needsPrefix && !hasPrefix) {
      newPath = `/${targetLang}${newPath}`;
    }

    return `${origin}${newPath}`;
  } catch {
    console.warn('Incorrect URL in metafield:', baseLink);
    return null;
  }
}

/**
 * Resolves the rewritten href for a store-switch link.
 *
 * @param {HTMLAnchorElement} link - Language menu link with data-store and data-lang.
 * @param {RedirectorContext} context - Current page and metafield context.
 * @returns {string|null} URL to apply, or null when the link should stay unchanged.
 */
function resolveStoreSwitchHref(link, context) {
  const targetLang = link.getAttribute('data-lang');
  const storeKey = resolveStoreKey(link);
  const targetStore = TARGET_STORES[storeKey];
  if (!targetLang || !targetStore) {
    return null;
  }

  const needsPrefix = targetLang !== targetStore.rootLang;

  if (storeKey === context.currentStoreKey) {
    return buildSameStoreHref(targetLang, needsPrefix, context);
  }

  if (context.isFallbackPage) {
    const targetPath = buildPrefixedPath(context.unprefixedPath, targetLang, needsPrefix);
    return `${targetStore.origin}${targetPath}`;
  }

  const datasetKey = context.metafieldMap[storeKey];
  const baseLink = datasetKey ? context.bodyData[datasetKey]?.trim() : '';
  if (!baseLink) {
    return null;
  }

  return buildAlternativeHref(baseLink, targetLang, needsPrefix, targetStore.origin);
}

/**
 * Applies the resolved alternative URL to a store-switch link.
 *
 * @param {HTMLAnchorElement} link - Language menu link.
 * @param {RedirectorContext} context - Current page and metafield context.
 * @returns {void}
 */
function applyStoreSwitchHref(link, context) {
  const href = resolveStoreSwitchHref(link, context);
  if (href) {
    link.setAttribute('href', href);
  }
  if (resolveStoreKey(link) === context.currentStoreKey) {
    link.removeAttribute('target');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const fallbackPaths = ['/collections', '/collections/all', '/cart', '/collections/', '/collections/all/', '/cart/'];
  const currentPath = window.location.pathname;
  const currentHost = window.location.hostname.replace(/^www\./, '');
  const hostConfig = METAFIELD_BY_HOST[currentHost] ? currentHost : DEFAULT_HOST;
  const currentStoreKey = HOST_TO_STORE[hostConfig];
  const currentLangPrefixes = collectLangPrefixes(currentStoreKey);
  const unprefixedPath = stripLocalePrefix(currentPath, currentLangPrefixes);

  const context = {
    bodyData: document.body.dataset,
    metafieldMap: METAFIELD_BY_HOST[hostConfig],
    isFallbackPage: fallbackPaths.includes(unprefixedPath),
    currentPath,
    currentStoreKey,
    currentLangPrefixes,
    unprefixedPath,
  };

  const links = document.querySelectorAll('#site-language a[data-lang]');
  links.forEach((link) => {
    applyStoreSwitchHref(link, context);
  });
});
